/**
 * Auth/config API. Customer config, user validation, IFS token.
 * Used by login thunks; no Bearer for config/validation, auth code for IFS token.
 */
import axios from "axios";
import CustomerConfig from "../../azure/CustomerConfig";
import AuthTeams from "../../auth/AuthTeams";
import { getApiClient } from "./client";
import { ENV_NAME } from "../constants/metaData";
import { logger } from "../../utils/logger";

/**
 * Get customer config (startup). Uses configuration API base URL; Customer-Key only.
 * @param {string} tenantId
 * @param {string} upn
 * @param {string} [env=ENV_NAME.dev] - Dev/QA/Test/Prod. Caller passes from process.env (REACT_APP_CONFIG_ENV or TEAMS_APP_ENV); see loginThunks.getConfigEnv().
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getCustomerConfig(tenantId, upn, env = ENV_NAME.dev) {
  const tid = tenantId ?? "";
  const userUpn = upn ?? "";
  logger.info("getCustomerConfig request", {
    tenantId: tid || "(empty)",
    upn: userUpn ? `${userUpn.slice(0, 3)}…` : "(empty)",
  }, "API");

  if (!tid && !userUpn) {
    const msg = "getCustomerConfig requires tenantId or UPN from Teams context (both are empty)";
    logger.error(msg, { tenantId: tid, upn: userUpn }, "API");
    const err = new Error(msg);
    err.code = "MISSING_TENANT_UPN";
    throw err;
  }

  const baseURL = new CustomerConfig().getConfigurationApi(env);
  const url =
    `${baseURL}api/Customer/get-customerconfig?tenantId=${encodeURIComponent(tid)}` +
    `&upn=${encodeURIComponent(userUpn)}`;
  const auth = new AuthTeams();
  const customerKey = auth.getCustomerKey() || process.env.REACT_APP_CLIENT_ID || "";
  try {
    const res = await axios.get(url, {
      headers: {
        "Customer-Key": customerKey,
      },
      timeout: 60000,
    });
    logger.info("getCustomerConfig success", { status: res.status }, "API");
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("getCustomerConfig failed", err, "API");
    if (err?.config?.url) logger.info("getCustomerConfig request URL", { url: err.config.url, status: err?.response?.status }, "API");
    throw err;
  }
}

/**
 * Validate IFS user. Service API; no Bearer, uses subscription/customer/user keys.
 * Uses its own axios instance without X-Client-Version to avoid version validation
 * during login — version check happens on post-auth API calls.
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getUserValidation() {
  logger.info("getUserValidation request", null, "API");
  const baseURL = new CustomerConfig().getServiceApi();
  const client = axios.create({ baseURL, timeout: 60000 });
  const auth = new AuthTeams();
  client.defaults.headers.common["Ocp-Apim-Subscription-Key"] =
    new CustomerConfig().getSubscriptionKey();
  client.defaults.headers.common["Customer-Key"] =
    auth.getCustomerKey() || process.env.REACT_APP_CLIENT_ID || "";
  client.defaults.headers.common["User-Key"] = new AuthTeams().getUserKey();
  const res = await client.get("api/User/get-uservalidation");
  logger.info("getUserValidation success", { status: res.status }, "API");
  return { data: res.data, status: res.status };
}

/**
 * Exchange IFS auth code for token. Service API; Authorization header = auth code (not Bearer).
 * User-Key = auth code [, IFS email from ifsUserDetails] — no Azure UPN.
 * @param {string} authCode - IFS auth code from authService.startLogin()
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getIfsToken(authCode) {
  const baseURL = new CustomerConfig().getServiceApi();
  const client = axios.create({ baseURL, timeout: 60000 });
  client.defaults.headers.common.authorization = authCode;
  client.defaults.headers.common["Ocp-Apim-Subscription-Key"] =
    new CustomerConfig().getSubscriptionKey();
  client.defaults.headers.common["Customer-Key"] =
    new AuthTeams().getCustomerKey();
  client.defaults.headers.common["User-Key"] = new AuthTeams().getUserKey();
  try {
    logger.info("getIfsToken request", null, "API");
    const res = await client.get("api/User/get-ifs-token");
    logger.info("getIfsToken success", { status: res.status }, "API");
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("getIfsToken failed", err, "API");
    throw err;
  }
}

/**
 * Get IFS login user details (WebUser, Identity, Description, etc.). Uses User-Key header.
 * Uses its own axios instance without X-Client-Version to avoid version validation
 * during login — version check happens on post-auth API calls.
 * @returns {Promise<{ data: object | null, status: number }>}
 */
export async function getIfsUserDetails() {
  logger.info("getIfsUserDetails request", null, "API");
  const baseURL = new CustomerConfig().getServiceApi();
  const client = axios.create({ baseURL, timeout: 60000 });
  const auth = new AuthTeams();
  client.defaults.headers.common["Ocp-Apim-Subscription-Key"] =
    new CustomerConfig().getSubscriptionKey();
  client.defaults.headers.common["Customer-Key"] =
    auth.getCustomerKey() || process.env.REACT_APP_CLIENT_ID || "";
  client.defaults.headers.common["User-Key"] = new AuthTeams().getUserKey();
  const res = await client.get("api/User/get-ifs-user");
  logger.info("getIfsUserDetails success", { status: res.status }, "API");
  return { data: res.data, status: res.status };
}

/**
 * Logout from IFS on backend and clear server-side cached token.
 * Uses standard API headers (subscription/customer/user keys).
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function logoutIfs() {
  logger.info("logoutIfs request", null, "API");
  const client = getApiClient(null);
  const res = await client.post("api/User/logout");
  logger.info("logoutIfs success", { status: res.status }, "API");
  return { data: res.data, status: res.status };
}
